// Position offer texts are stored as Quill Deltas (JSON). Older texts and texts coming from
// product configurations are plain text. See Application/Services/Offers/Common/OfferTextRenderer.cs.

interface IDeltaOperation {
  insert?: any;
  attributes?: { [key: string]: any };
}

export interface IDelta {
  ops: IDeltaOperation[];
}

const parseRichOfferText = (value: string | null): IDelta | null => {
  if (value === null || value === undefined) {
    return null;
  }

  const trimmed = value.trim();
  if (!trimmed.startsWith('{')) {
    return null;
  }

  try {
    const parsed = JSON.parse(trimmed);
    return parsed !== null && Array.isArray(parsed.ops) ? parsed : null;
  } catch (error) {
    return null;
  }
};

const plainTextToDelta = (text: string): IDelta => ({
  ops: [{ insert: text.endsWith('\n') ? text : `${text}\n` }],
});

const offerTextToDelta = (value: string | null): IDelta =>
  parseRichOfferText(value) ?? plainTextToDelta(value ?? '');

// Appends plain text (e.g. the offer text of an attached product configuration) as new lines
const appendPlainTextToOfferText = (
  current: string | null,
  text: string
): string => {
  if (text === null || text === undefined || text === '') {
    return current ?? '';
  }

  const rich = parseRichOfferText(current);

  if (rich === null) {
    return current !== null && current !== undefined && current.length > 0
      ? `${current}\n${text}`
      : text;
  }

  return JSON.stringify({
    ops: [...rich.ops, { insert: text.endsWith('\n') ? text : `${text}\n` }],
  });
};

// Appends one offer text (rich or plain) after another, keeping the formatting of both
const appendOfferText = (current: string | null, addition: string): string => {
  if (current === null || current === undefined || current.trim() === '') {
    return addition;
  }

  return JSON.stringify({
    ops: [...offerTextToDelta(current).ops, ...offerTextToDelta(addition).ops],
  });
};

// Plain text of an offer text, e.g. for a short preview
const offerTextToPlainText = (value: string | null): string =>
  offerTextToDelta(value)
    .ops.map((op) => (typeof op.insert === 'string' ? op.insert : ''))
    .join('')
    .trim();

export {
  appendOfferText,
  appendPlainTextToOfferText,
  offerTextToDelta,
  offerTextToPlainText,
  parseRichOfferText,
};
